import {InboundBridgeBucketRecord} from './index';
import React from 'react';
import {BarChart, Box, Container, Header, MixedLineBarChartProps, Spinner} from '@amzn/awsui-components-react';

const COMPONENT_TEXTS = {
    chart_description:
        'The Weighted Absolute Percentage Error (WAPE) contribution metrics have been normalized to align with the total WAPE. Top 10 biggest contributors are shown.',
} as const;

export function InboundBridgeStackedBarChart({data, isLoading}: {data: InboundBridgeBucketRecord[]; isLoading: boolean}) {
    if (isLoading) {
        return (
            <Container
                header={
                    <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                        Daily WAPE Contribution
                    </Header>
                }
            >
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            </Container>
        );
    }

    const renderData = transformData(data);
    const series = buildSeries(renderData);

    return (
        <Container
            header={
                <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                    Daily WAPE Contribution
                </Header>
            }
        >
            <BarChart
                series={series}
                xScaleType="categorical"
                yScaleType="linear"
                stackedBars
                hideFilter
                xTitle="Date"
                yTitle="WAPE Contribution"
                yTickFormatter={(v) => (v * 100).toFixed(0) + '%'}
                height={400}
                legendTitle="Bucket"
                empty={
                    <Box color="inherit" textAlign="center">
                        <Box fontWeight="bold">No data available</Box>
                        <Box color="inherit" variant="p">
                            There is no data for the selected filters.
                        </Box>
                    </Box>
                }
                noMatch={
                    <Box color="inherit" textAlign="center">
                        <Box fontWeight="bold">No matching data</Box>
                        <Box color="inherit" variant="p">
                            There is no data matching your filter criteria.
                        </Box>
                    </Box>
                }
            />
        </Container>
    );
}

export function buildSeries(data: BucketData): MixedLineBarChartProps.BarDataSeries<string>[] {
    return data.buckets.map((bucket) => ({
        title: bucket.name,
        type: 'bar',
        data: data.dates.map((date, i) => ({
            x: date,
            y: bucket.wapeContribsRescaled[i] ?? 0,
        })),
        valueFormatter: (v: number) => (v * 100).toFixed(2) + '%',
    }));
}

export function transformData(records: InboundBridgeBucketRecord[]): BucketData {
    records = records.filter((record: InboundBridgeBucketRecord) => record.wapeContribRescaled !== null);

    const dates = Object.keys(
        records.reduce((acc: Record<string, boolean>, record: InboundBridgeBucketRecord) => {
            acc[record.date] = true;
            return acc;
        }, {})
    ).sort();

    interface BucketGroup {
        seqId: number;
        name: string;
        values: Record<string, number>;
    }

    const topBucketsPerDate = dates.flatMap((date) =>
        records
            .filter((record) => record.date === date)
            .sort((a, b) => (b.wapeContribRescaled || 0) - (a.wapeContribRescaled || 0))
            .slice(0, 10)
            .sort((a, b) => a.seqId - b.seqId)
    );

    const bucketGroups = topBucketsPerDate.reduce((acc: Record<string, BucketGroup>, record: InboundBridgeBucketRecord) => {
        const key = `${record.seqId}-${record.name}`;
        if (!acc[key]) {
            acc[key] = {seqId: record.seqId, name: record.name, values: {}};
        }
        acc[key].values[record.date] = record.wapeContribRescaled || 0;
        return acc;
    }, {});

    const buckets = Object.keys(bucketGroups).map((key) => {
        const bucket = bucketGroups[key];
        return {
            seqId: bucket.seqId,
            name: bucket.name,
            wapeContribsRescaled: dates.map((date) => bucket.values[date] || 0),
        };
    });

    return {dates, buckets};
}

export type BucketData = {
    dates: string[];
    buckets: {
        seqId: number;
        name: string;
        wapeContribsRescaled: number[];
    }[];
};
